import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  MicroagentListResponse,
  PairingGenerateResponse,
  PairingStatusResponse,
  AgentLogsResponse,
  CommandResponse,
  AgentToolsResponse,
} from '@/types/microagent';
import { getAccessToken } from '@/utils/jwt';

export const microagentApi = createApi({
  reducerPath: 'microagentApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_AGENT_MCP_URL || '/agent',
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Microagent', 'PairingStatus', 'Logs'],
  endpoints: (builder) => ({
    listMicroagents: builder.query<MicroagentListResponse, undefined>({
      query: () => '/microagent/agents',
      providesTags: ['Microagent'],
    }),

    generatePairingCode: builder.mutation<PairingGenerateResponse, { user_id?: string }>({
      query: (body) => ({
        url: '/microagent/pair',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PairingStatus'],
    }),

    getPairingStatus: builder.query<PairingStatusResponse, string>({
      query: (userId) => `/microagent/pair/status?user_id=${encodeURIComponent(userId)}`,
      providesTags: ['PairingStatus'],
    }),

    sendCommand: builder.mutation<
      CommandResponse,
      { agent_id: string; action: string; payload: Record<string, unknown> }
    >({
      query: (body) => ({
        url: '/microagent/command',
        method: 'POST',
        body,
      }),
    }),

    getAgentLogs: builder.query<AgentLogsResponse, { agentId: string; limit?: number }>({
      query: ({ agentId, limit = 100 }) =>
        `/microagent/agents/${encodeURIComponent(agentId)}/logs?limit=${String(limit)}`,
      providesTags: (_result, _err, { agentId }) => [{ type: 'Logs' as const, id: agentId }],
    }),

    getAgentTools: builder.query<AgentToolsResponse, string>({
      query: (agentId) => `/microagent/agents/${encodeURIComponent(agentId)}/tools`,
      providesTags: (_result, _err, agentId) => [{ type: 'Microagent' as const, id: agentId }],
    }),
  }),
});

export const {
  useListMicroagentsQuery,
  useGeneratePairingCodeMutation,
  useGetPairingStatusQuery,
  useSendCommandMutation,
  useGetAgentLogsQuery,
  useGetAgentToolsQuery,
} = microagentApi;
